 
import { useState, useCallback } from "react";

 
interface DateRange { startDate: string; endDate: string }

interface ExportConfig {
  ficheName?:          string;
  locationName?:       string;
  dateRange:           DateRange;
  compareEnabled?:     boolean;
  compareRange?:       DateRange;
  primaryPeriodLabel?:  string;
  comparePeriodLabel?:  string;

   ficheFull?:          any;
  ficheFull2?:         any;
  regionData?:         any;
  regionData2?:        any;
  regionReviews?:      any;
  isRegion?:           boolean;
}

 
const fmt = (n: number | undefined | null): string => {
  if (n == null) return "—";
  return n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(2)}M`
    : n >= 1_000
    ? `${(n / 1_000).toFixed(1)}k`
    : String(Math.round(n));
};

const fmtRaw = (n: number | undefined | null) =>
  n == null ? "" : String(Math.round(n));

const delta = (cur: number, prev: number): string => {
  if (!prev) return "";
  const pct = Math.round(((cur - prev) / prev) * 100);
  return pct >= 0 ? `+${pct}%` : `${pct}%`;
};

const csvEscape = (v: string | number) =>
  `"${String(v).replace(/"/g, '""')}"`;

const buildCSVRow = (cells: (string | number)[]) =>
  cells.map(csvEscape).join(",");

 const triggerDownload = (content: string, filename: string, mime: string) => {
  const blob = new Blob(["\uFEFF" + content], { type: mime });
  const url  = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

   setTimeout(() => URL.revokeObjectURL(url), 1000);
};
 
const buildCSV = (cfg: ExportConfig): string => {
  const {
    ficheName, locationName, dateRange, compareEnabled, compareRange,
    primaryPeriodLabel = "Période A", comparePeriodLabel = "Période B",
    ficheFull, ficheFull2, regionData, regionData2, regionReviews, isRegion,
  } = cfg;

  const name  = ficheName || locationName || "Rapport";
  const lines: string[] = [];

  const sep   = () => lines.push(""); // blank line between sections
  const title = (t: string) => lines.push(buildCSVRow([t]));
  const head  = (...cols: string[]) => lines.push(buildCSVRow(cols));
  const row   = (...cols: (string | number)[]) => lines.push(buildCSVRow(cols));

  // ── Meta ──
  title(`Rapport de performance GBP — ${name}`);
  row("Période principale", `${dateRange.startDate} → ${dateRange.endDate}`);
  if (compareEnabled && compareRange)
    row("Période de comparaison", `${compareRange.startDate} → ${compareRange.endDate}`);
  row("Généré le", new Date().toLocaleString("fr-FR"));
  sep();

  // ── Helper: get kpis from either region or fiche data ──
  const kpisA = isRegion ? regionData?.overviewData?.kpis  : ficheFull?.kpis;
  const kpisB = isRegion ? regionData2?.overviewData?.kpis : ficheFull2?.kpis;

  // ── 1. KPI Summary ──
  title("1. Résumé des KPIs");

  if (compareEnabled && kpisB) {
    head("Indicateur", primaryPeriodLabel, comparePeriodLabel, "Variation");
    const kpiRows = [
      ["Impressions totales",  kpisA?.totalImpressions,  kpisB?.totalImpressions],
      ["Actions totales",      kpisA?.totalActions,      kpisB?.totalActions],
      ["Appels",               kpisA?.callClicks,        kpisB?.callClicks],
      ["Clics site web",       kpisA?.websiteClicks,     kpisB?.websiteClicks],
      ["Itinéraires",          kpisA?.directionRequests, kpisB?.directionRequests],
      ["Réservations",         kpisA?.bookings,          kpisB?.bookings],
      ["Conversations",        kpisA?.conversations,     kpisB?.conversations],
    ] as [string, number, number][];
    kpiRows.forEach(([label, a, b]) =>
      row(label, fmtRaw(a), fmtRaw(b), delta(a, b))
    );
  } else {
    head("Indicateur", "Valeur");
    row("Impressions totales",  fmtRaw(kpisA?.totalImpressions));
    row("Actions totales",      fmtRaw(kpisA?.totalActions));
    row("Appels",               fmtRaw(kpisA?.callClicks));
    row("Clics site web",       fmtRaw(kpisA?.websiteClicks));
    row("Itinéraires",          fmtRaw(kpisA?.directionRequests));
    row("Réservations",         fmtRaw(kpisA?.bookings));
    row("Conversations",        fmtRaw(kpisA?.conversations));
  }
  sep();

  // ── 2. Traffic sources ──
  title("2. Sources de trafic (impressions)");
  const seriesA = isRegion ? regionData?.overviewData?.series  : ficheFull?.series;
  const seriesB = isRegion ? regionData2?.overviewData?.series : ficheFull2?.series;

  const IMP_KEYS: Record<string, string> = {
    "BUSINESS_IMPRESSIONS_DESKTOP_MAPS":   "Desktop Maps",
    "BUSINESS_IMPRESSIONS_DESKTOP_SEARCH": "Desktop Search",
    "BUSINESS_IMPRESSIONS_MOBILE_MAPS":    "Mobile Maps",
    "BUSINESS_IMPRESSIONS_MOBILE_SEARCH":  "Mobile Search",
  };

  const sumSeries = (s: any, k: string): number =>
    (s?.[k] || []).reduce((a: number, p: any) => a + (p.value ?? 0), 0);

  if (compareEnabled && seriesB) {
    head("Source", primaryPeriodLabel, comparePeriodLabel, "Variation");
    Object.entries(IMP_KEYS).forEach(([k, label]) => {
      const a = sumSeries(seriesA, k);
      const b = sumSeries(seriesB, k);
      row(label, fmtRaw(a), fmtRaw(b), delta(a, b));
    });
  } else {
    head("Source", "Impressions");
    Object.entries(IMP_KEYS).forEach(([k, label]) =>
      row(label, fmtRaw(sumSeries(seriesA, k)))
    );
  }
  sep();

  // ── 3. Daily trend (time series) ──
  title("3. Évolution journalière");
  const trendA = isRegion ? regionData?.trendsData  : ficheFull?.trends;
  const trendB = isRegion ? regionData2?.trendsData : ficheFull2?.trends;

  if (trendA?.length) {
    const metricKeys = Object.keys(IMP_KEYS);
    const metricLabels = Object.values(IMP_KEYS);

    if (compareEnabled && trendB?.length) {
      head("Date (A)", ...metricLabels.map(l => `${l} (A)`), "Date (B)", ...metricLabels.map(l => `${l} (B)`));
      const maxLen = Math.max(trendA.length, trendB.length);
      for (let i = 0; i < maxLen; i++) {
        const da = trendA[i];
        const db = trendB[i];
        row(
          da?.date ?? "",
          ...metricKeys.map(k => fmtRaw(da?.[k])),
          db?.date ?? "",
          ...metricKeys.map(k => fmtRaw(db?.[k])),
        );
      }
    } else {
      head("Date", ...metricLabels);
      trendA.forEach((d: any) =>
        row(d.date, ...metricKeys.map(k => fmtRaw(d[k])))
      );
    }
    sep();
  }

  // ── 4. Reviews summary ──
  const reviewsRaw = isRegion ? regionReviews : ficheFull?.reviews;
  if (reviewsRaw) {
    title("4. Avis clients");
    const avg   = reviewsRaw.averageRating ?? reviewsRaw.average_rating ?? null;
    const total = reviewsRaw.totalCount    ?? reviewsRaw.total_count    ?? 0;
    const dist  = reviewsRaw.distribution  ?? {};
    row("Note moyenne", avg != null ? String(parseFloat(avg).toFixed(1)) : "—");
    row("Nombre total d'avis", fmtRaw(total));
    sep();
    head("Étoiles", "Nombre d'avis");
    [5, 4, 3, 2, 1].forEach(s => row(`${s} étoile${s > 1 ? "s" : ""}`, fmtRaw(dist[s] ?? 0)));

    const reviews = reviewsRaw.list ?? reviewsRaw.reviews ?? [];
    if (reviews.length) {
      sep();
      title("4b. Détail des avis récents");
      head("Auteur", "Note", "Date", "Commentaire", "Réponse propriétaire");
      reviews.slice(0, 50).forEach((r: any) => {
        const stars = typeof r.starRating === "number"
          ? r.starRating
          : { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 }[r.starRating as string] ?? "";
        row(
          r.reviewer?.displayName ?? "Anonyme",
          stars,
          r.createTime ? new Date(r.createTime).toLocaleDateString("fr-FR") : "",
          r.comment ?? "",
          r.reviewReply?.comment ?? "",
        );
      });
    }
    sep();
  }

  // ── 5. Keywords (fiche only) ──
  const keywords = ficheFull?.keywords;
  if (!isRegion && keywords?.length) {
    title("5. Mots-clés de recherche");
    const kwB = ficheFull2?.keywords as any[] | undefined;
    if (compareEnabled && kwB?.length) {
      const prevMap = new Map(kwB.map((k: any) => [k.keyword, k.impressions]));
      head("Mot-clé", `Impressions (${primaryPeriodLabel})`, `Impressions (${comparePeriodLabel})`, "Variation");
      keywords
        .sort((a: any, b: any) => b.impressions - a.impressions)
        .slice(0, 30)
        .forEach((kw: any) => {
          const prev = prevMap.get(kw.keyword) as number | undefined;
          row(kw.keyword, fmtRaw(kw.impressions), fmtRaw(prev), prev != null ? delta(kw.impressions, prev) : "");
        });
    } else {
      head("Mot-clé", "Impressions");
      keywords
        .sort((a: any, b: any) => b.impressions - a.impressions)
        .slice(0, 30)
        .forEach((kw: any) => row(kw.keyword, fmtRaw(kw.impressions)));
    }
  }

  return lines.join("\n");
};

 
const buildHTMLReport = (cfg: ExportConfig): string => {
  const {
    ficheName, locationName, dateRange, compareEnabled, compareRange,
    primaryPeriodLabel = "Période A", comparePeriodLabel = "Période B",
    ficheFull, ficheFull2, regionData, regionData2, regionReviews, isRegion,
  } = cfg;

  const name  = ficheName || locationName || "Rapport";
  const kpisA = isRegion ? regionData?.overviewData?.kpis  : ficheFull?.kpis;
  const kpisB = isRegion ? regionData2?.overviewData?.kpis : ficheFull2?.kpis;
  const seriesA = isRegion ? regionData?.overviewData?.series  : ficheFull?.series;
  const seriesB = isRegion ? regionData2?.overviewData?.series : ficheFull2?.series;
  const trendA  = isRegion ? regionData?.trendsData  : ficheFull?.trends;
  const reviewsRaw = isRegion ? regionReviews : ficheFull?.reviews;

  const IMP_KEYS: [string, string][] = [
    ["BUSINESS_IMPRESSIONS_DESKTOP_MAPS",   "Desktop Maps"],
    ["BUSINESS_IMPRESSIONS_DESKTOP_SEARCH", "Desktop Search"],
    ["BUSINESS_IMPRESSIONS_MOBILE_MAPS",    "Mobile Maps"],
    ["BUSINESS_IMPRESSIONS_MOBILE_SEARCH",  "Mobile Search"],
  ];
  const sumSeries = (s: any, k: string): number =>
    (s?.[k] || []).reduce((a: number, p: any) => a + (p.value ?? 0), 0);

  const kpiDefs = [
    { label: "Impressions",  keyA: "totalImpressions" },
    { label: "Actions",      keyA: "totalActions"  },
    { label: "Appels",       keyA: "callClicks"  },
    { label: "Site web",     keyA: "websiteClicks"  },
    { label: "Itinéraires",  keyA: "directionRequests"  },
    { label: "Réservations", keyA: "bookings"          },
  ];

  const deltaHtml = (cur: number, prev: number) => {
    if (!prev) return "";
    const pct = Math.round(((cur - prev) / prev) * 100);
    const color = pct > 0 ? "#166534" : pct < 0 ? "#991b1b" : "#64748b";
    const bg    = pct > 0 ? "#f0fdf4" : pct < 0 ? "#fef2f2" : "#f8fafc";
    return `<span style="display:inline-block;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:600;background:${bg};color:${color};margin-left:8px;">${pct >= 0 ? "▲ +" : "▼ "}${pct}%</span>`;
  };

  const kpiCards = kpiDefs.map(({ label, keyA }) => {
    const a = kpisA?.[keyA] ?? 0;
    const b = kpisB?.[keyA];
    return `
      <div style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:16px 20px;flex:1;min-width:130px;">
         <p style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:.06em;margin:0 0 4px;">${label}</p>
        <p style="font-size:26px;font-weight:700;color:#0f172a;margin:0;line-height:1.1;">${fmt(a)}</p>
        ${compareEnabled && b != null ? `<p style="font-size:11px;color:#94a3b8;margin:6px 0 0;">vs ${fmt(b)}${deltaHtml(a, b)}</p>` : ""}
      </div>`;
  }).join("");

  const trafficRows = IMP_KEYS.map(([k, label]) => {
    const a = sumSeries(seriesA, k);
    const b = sumSeries(seriesB, k);
    return `<tr>
      <td style="padding:8px 12px;color:#334155;">${label}</td>
      <td style="padding:8px 12px;text-align:right;font-weight:600;">${fmt(a)}</td>
      ${compareEnabled ? `<td style="padding:8px 12px;text-align:right;color:#7c3aed;">${fmt(b)}</td><td style="padding:8px 12px;text-align:right;">${deltaHtml(a, b)}</td>` : ""}
    </tr>`;
  }).join("");

  const reviewsSection = (() => {
    if (!reviewsRaw) return "";
    const avg   = reviewsRaw.averageRating ?? reviewsRaw.average_rating;
    const total = reviewsRaw.totalCount    ?? reviewsRaw.total_count ?? 0;
    const dist  = reviewsRaw.distribution ?? {};
    const starsHtml = [5, 4, 3, 2, 1].map(s => {
      const count = dist[s] ?? 0;
      const pct   = total ? Math.round((count / total) * 100) : 0;
      return `<div style="display:flex;align-items:center;gap:10px;margin-bottom:6px;">
        <span style="font-size:12px;color:#64748b;width:60px;">★ ${s} étoile${s > 1 ? "s" : ""}</span>
        <div style="flex:1;height:8px;background:#f1f5f9;border-radius:4px;overflow:hidden;">
          <div style="height:100%;width:${pct}%;background:#f59e0b;border-radius:4px;"></div>
        </div>
        <span style="font-size:12px;font-weight:600;width:28px;text-align:right;">${count}</span>
      </div>`;
    }).join("");

    return `
      <h2 style="font-size:16px;font-weight:700;color:#0f172a;margin:32px 0 16px;">Avis clients</h2>
      <div style="display:flex;gap:24px;align-items:flex-start;flex-wrap:wrap;">
        <div style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:24px;text-align:center;min-width:120px;">
          <p style="font-size:48px;font-weight:800;color:#0f172a;margin:0;line-height:1;">${avg ? parseFloat(avg).toFixed(1) : "—"}</p>
          <p style="color:#f59e0b;font-size:20px;margin:8px 0 4px;">★★★★★</p>
          <p style="font-size:12px;color:#94a3b8;margin:0;">${fmt(total)} avis</p>
        </div>
        <div style="flex:1;min-width:220px;">${starsHtml}</div>
      </div>`;
  })();

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>Rapport GBP — ${name}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; background:#f8fafc; color:#1e293b; margin:0; padding:0; }
    .page { max-width: 900px; margin: 0 auto; padding: 40px 32px; }
    table { width:100%; border-collapse:collapse; }
    th { background:#f1f5f9; color:#475569; font-size:11px; text-transform:uppercase; letter-spacing:.06em; padding:8px 12px; text-align:left; }
    tr:nth-child(even) td { background:#f8fafc; }
    @media print {
      body { background: white; }
      .page { padding: 20px; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
<div class="page">

  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;border-bottom:2px solid #e2e8f0;padding-bottom:24px;">
    <div>
      <h1 style="font-size:24px;font-weight:800;color:#0f172a;margin:0 0 6px;">Rapport de performance GBP</h1>
      <p style="color:#64748b;font-size:14px;margin:0;">${name}</p>
    </div>
    <div style="text-align:right;">
      <div style="display:inline-flex;align-items:center;gap:8px;padding:6px 14px;background:#eff6ff;border-radius:8px;margin-bottom:4px;">
        <span style="width:8px;height:8px;background:#3b82f6;border-radius:50%;display:inline-block;"></span>
        <span style="font-size:12px;font-weight:600;color:#1d4ed8;">${primaryPeriodLabel}</span>
        <span style="font-size:12px;color:#93c5fd;">${dateRange.startDate} → ${dateRange.endDate}</span>
      </div>
      ${compareEnabled && compareRange ? `
      <div style="display:inline-flex;align-items:center;gap:8px;padding:6px 14px;background:#f5f3ff;border-radius:8px;">
        <span style="width:8px;height:8px;background:#7c3aed;border-radius:50%;display:inline-block;"></span>
        <span style="font-size:12px;font-weight:600;color:#6d28d9;">${comparePeriodLabel}</span>
        <span style="font-size:12px;color:#c4b5fd;">${compareRange.startDate} → ${compareRange.endDate}</span>
      </div>` : ""}
      <p style="font-size:11px;color:#94a3b8;margin:8px 0 0;">Généré le ${new Date().toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" })}</p>
    </div>
  </div>

  <h2 style="font-size:16px;font-weight:700;color:#0f172a;margin:0 0 16px;">Indicateurs clés</h2>
  <div style="display:flex;gap:12px;flex-wrap:wrap;margin-bottom:32px;">${kpiCards}</div>

  <h2 style="font-size:16px;font-weight:700;color:#0f172a;margin:0 0 16px;">Sources de trafic</h2>
  <div style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;overflow:hidden;margin-bottom:32px;">
    <table>
      <thead><tr>
        <th>Source</th>
        <th style="text-align:right;">${primaryPeriodLabel}</th>
        ${compareEnabled ? `<th style="text-align:right;">${comparePeriodLabel}</th><th style="text-align:right;">Variation</th>` : ""}
      </tr></thead>
      <tbody>${trafficRows}</tbody>
    </table>
  </div>

  ${trendA?.length ? `
  <h2 style="font-size:16px;font-weight:700;color:#0f172a;margin:0 0 16px;">Évolution journalière</h2>
  <div style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;overflow:hidden;margin-bottom:32px;">
    <table>
      <thead><tr>
        <th>Date</th>
        ${IMP_KEYS.map(([, l]) => `<th style="text-align:right;">${l}</th>`).join("")}
      </tr></thead>
      <tbody>
        ${trendA.slice(0, 90).map((d: any) => `<tr>
          <td style="padding:6px 12px;color:#64748b;font-size:12px;">${d.date}</td>
          ${IMP_KEYS.map(([k]) => `<td style="padding:6px 12px;text-align:right;font-size:12px;">${d[k] != null ? Math.round(d[k]) : "—"}</td>`).join("")}
        </tr>`).join("")}
      </tbody>
    </table>
  </div>` : ""}

  ${reviewsSection}

  <p style="text-align:center;font-size:11px;color:#cbd5e1;margin-top:48px;padding-top:16px;border-top:1px solid #f1f5f9;">
    Rapport généré automatiquement · ${new Date().toLocaleString("fr-FR")}
  </p>
</div>
<script>window.print && window.print();</script>
</body>
</html>`;
};

 
export const useGBPExport = (cfg: ExportConfig) => {
  const [exporting, setExporting] = useState(false);

  const slugName = (cfg.ficheName || cfg.locationName || "rapport")
    .replace(/[^a-z0-9]/gi, "_")
    .toLowerCase();

  const period = cfg.dateRange.startDate.replace(/-/g, "") + "_" + cfg.dateRange.endDate.replace(/-/g, "");

const exportCSV = useCallback(async () => {
  if (exporting) return;

  setExporting(true);

  try {
    await new Promise(r => setTimeout(r, 0)); 

    const csv = buildCSV(cfg);
    triggerDownload(csv, `gbp_${slugName}_${period}.csv`, "text/csv;charset=utf-8;");
  } finally {
    setExporting(false);
  }
}, [cfg, exporting, slugName, period]);
  const exportReport = useCallback(() => {
    setExporting(true);
    try {
      const html = buildHTMLReport(cfg);
      const win  = window.open("", "_blank");
      if (win) {
        win.document.write(html);
        win.document.close();
      } else {
         triggerDownload(html, `gbp_${slugName}_${period}.html`, "text/html;charset=utf-8;");
      }
    } finally {
      setTimeout(() => setExporting(false), 800);
    }
  }, [cfg, slugName, period]);

  return { exporting, exportCSV, exportReport };
};